import { roundDecimals } from "./formatNumbers";
import { addMoney, resolveMoneyDecimals, roundMoney, subtractMoney, sumMoney } from "./money";

const toSafeNumber = (value, country = null) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value !== "string") return 0;

  const cleaned = value.trim().replaceAll(/[^\d,.-]/g, "");
  if (!cleaned) return 0;

  if (country?.decimalSeparator && country?.thousandSeparator) {
    const { decimalSeparator, thousandSeparator } = country;
    const thousandRegex = new RegExp(`\\${thousandSeparator}`, "g");
    const decimalRegex = new RegExp(`\\${decimalSeparator}`, "g");

    const normalizedByCountry = cleaned.replaceAll(thousandRegex, "").replaceAll(decimalRegex, ".");

    const parsedByCountry = Number(normalizedByCountry);
    return Number.isFinite(parsedByCountry) ? parsedByCountry : 0;
  }

  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  const hasComma = lastComma !== -1;
  const hasDot = lastDot !== -1;
  const decimalSeparator = hasComma && (!hasDot || lastComma > lastDot) ? "," : ".";

  const normalized = cleaned
    .replaceAll(decimalSeparator === "," ? "." : ",", "")
    .replaceAll(decimalSeparator, ".");

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * 🇪🇨 ECUADOR - Ajuste de facturas con bug de datos (Business Central)
 * --------------------------------------------------------------
 * Esta función fue solicitada por el equipo de Ecuador como solución
 * temporal a un conjunto de facturas con errores en los descuentos
 * (importadas desde Business Central) que **no pueden ser modificadas
 * directamente en BC** debido a restricciones de sincronización.
 *
 * Descripción:
 * Corrige el total de una factura aplicando ajustes porcentuales
 * adicionales cuando se detectan diferencias conocidas de cálculo:
 *
 *  - Aplica un **4% adicional por línea** si la línea tiene un descuento > 4%.
 *    (casos en los que originalmente se había aplicado 54%, 44.55%, 8.35%, etc.)
 *
 *  - Mantiene el total original cuando la diferencia entre el total y el pago
 *    es menor o igual a ±0.02 (tolerancia numérica).
 *
 * Nota:
 * Este ajuste es solo para permitir que den los valores para crear una nota de credito

 * @param {Object} invoice        Factura seleccionada (con sus líneas)
 * @param {number} paymentTotal   Monto total pagado registrado en caja
 * @param {number} total          Total original calculado por el sistema
 * @param {number} pendingRaw     Diferencia entre total y total pagado
 * @param {Array} paymentLines    Líneas de pago asociadas
 * @param {Object} country        País activo (para precisión de cálculo, ver money.js)
 *
 * @returns {Object} Objeto con:
 *  - {number} totalAjustado  → Total recalculado con los ajustes aplicados
 *  - {number} ajuste         → Diferencia detectada entre pago y total
 *
 **/
export const applyConditionalAdjustment = (
  invoice,
  paymentTotal,
  total,
  pendingRaw,
  paymentLines,
  country = null
) => {
  // Normalizamos todos los valores a positivos
  const absPaymentTotal = Math.abs(paymentTotal || 0);
  const absTotal = Math.abs(total || 0);
  const absPending = Math.abs(pendingRaw || 0);

  if (!invoice || !invoice.lines || invoice.lines.length === 0) {
    return {
      totalAjustado: 0,
      ajuste: 0,
    };
  }

  //si el total esta dentro de la tolerancia no se realizan cambios
  if (absPending <= 0.02) {
    return {
      totalAjustado: absTotal,
      ajuste: 0,
    };
  }

  // Verificar si hay líneas con descuento > 4 %
  const hasExtraDiscount =
    invoice.lines.some((line) => (line.Line_Discount_ || 0) > 4) ||
    (invoice.lines.some((line) => (line.Line_Discount_ || 0) >= 4) && paymentLines.length > 1);

  if (!hasExtraDiscount) {
    return { totalAjustado: absTotal, ajuste: 0 };
  }

  // Calcular la diferencia real entre pago y total
  const decimals = resolveMoneyDecimals(country);
  const ajuste = roundMoney(absPaymentTotal - absTotal, decimals);
  const totalAjustado = roundMoney(absPaymentTotal - ajuste, decimals);

  return { totalAjustado, ajuste };
};

const getEmptyTotals = () => ({
  subtotal: 0,
  vat: 0,
  baseTotal: 0,
  total: 0,
  paymentTotal: 0,
  pending: 0,
  totalAjustado: 0,
  ajuste: 0,
  paymentMethodDiscount: 0,
});

/**
 * Calcula el precio unitario que se debe enviar a Business Central.
 *
 * IMPORTANTE:
 * - Business Central SIEMPRE espera el precio unitario ORIGINAL
 *   → SIN IVA
 *   → SIN DESCUENTOS aplicados
 *
 * - El IVA y los descuentos (% de línea y de cabecera) los recalcula
 *   Business Central internamente al recibir la factura.
 *
 * - Esta función NO debe usarse para mostrar importes en pantalla
 *   ni para imprimir tickets.
 *
 * - Si el precio original incluye IVA, aquí se elimina para obtener
 *   la base imponible real.
 *
 * @param {number} unitPrice - Precio unitario original (puede incluir IVA)
 * @param {number} ivaPct - Porcentaje de IVA del producto
 * @param {boolean} priceIncludesVAT - Indica si el precio incluye IVA
 * @param {Object} country - País activo (para precisión de cálculo, ver money.js)
 * @returns {number} Precio unitario SIN IVA y SIN descuento
 */
export const calculateBCUnitPrice = ({
  unitPrice = 0,
  ivaPct = 0,
  priceIncludesVAT = false,
  country = null,
}) => {
  const decimals = resolveMoneyDecimals(country);
  if (priceIncludesVAT) {
    return roundDecimals(unitPrice / (1 + ivaPct / 100), decimals);
  }
  return roundDecimals(unitPrice, decimals);
};

/**
 * Calcula los importes de una línea de factura o nota de crédito.
 * Soporta precios con y sin IVA.
 *
 * @param {Object} params
 * @param {number} params.unitPrice         Precio unitario
 * @param {number} params.qty               Cantidad (puede ser negativa)
 * @param {number} params.discountPct       Descuento en %
 * @param {number} params.ivaPct            IVA en %
 * @param {boolean} params.priceIncludesVAT Indica si el precio incluye IVA
 * @param {Object} [params.country]         País activo (para precisión de cálculo, ver money.js)
 *
 * @returns {{
 *   base: number,       // Importe sin IVA
 *   vat: number,        // IVA calculado
 *   total: number,      // Importe final con IVA
 *   discount: number    // Descuento de línea aplicado
 * }}
 */
export const calculateLineAmounts = ({
  unitPrice = 0,
  qty = 0,
  discountPct = 0,
  ivaPct = 0,
  priceIncludesVAT = false,
  country = null,
}) => {
  const safeUnitPrice = toSafeNumber(unitPrice, country);
  const safeQty = toSafeNumber(qty, country);
  const safeDiscountPct = toSafeNumber(discountPct, country);
  const safeIvaPct = toSafeNumber(ivaPct, country);
  const decimals = resolveMoneyDecimals(country);

  const discountedPrice = safeUnitPrice * (1 - safeDiscountPct / 100);
  const discount = (safeUnitPrice - discountedPrice) * safeQty;

  let base = 0;
  let vat = 0;
  let total = 0;

  if (priceIncludesVAT) {
    total = safeQty * discountedPrice;
    base = total / (1 + safeIvaPct / 100);
    vat = total - base;
  } else {
    base = safeQty * discountedPrice;
    vat = base * (safeIvaPct / 100);
    total = base + vat;
  }

  return {
    base: roundDecimals(base, decimals),
    vat: roundDecimals(vat, decimals),
    total: roundDecimals(total, decimals),
    discount: roundDecimals(discount, decimals),
  };
};

/**
 * Los importes que Business Central ya calculó para esta línea, o `null` si la
 * línea no viene de ahí.
 *
 * `Amount_Including_VAT` sólo lo escriben los mapeadores de Traer Históricos
 * (utils/syncUtils.js), así que su presencia es la marca de "esto lo calculó
 * BC". `Line_Amount` es la base ya con el descuento aplicado, y la diferencia
 * entre los dos es el IVA real de la línea -- el mismo criterio que usan los
 * informes sobre las líneas crudas de BC.
 *
 * Se prefiere a rehacer la cuenta porque rehacerla se desvía: partir de
 * `Unit_Price x Cantidad x (1-dto%) x (1+iva%)` redondea cada línea por
 * separado y pierde un centavo cada vez que el resultado cae justo en el medio
 * (39,58 al 15% da 45,517, y BC guardó 45,51). En una factura de seis líneas
 * eso llega a 0,03 y el Pendiente se sale de la tolerancia de 0,02 sin que
 * nada esté mal. Medido contra el ambiente real: de los descuadres con pago
 * propio, la mayoría eran exactamente de 0,03-0,04.
 */
const importesDeBusinessCentral = (line, decimals) => {
  if (line?.Amount_Including_VAT == null) return null;

  const conIva = Number(line.Amount_Including_VAT);
  const base = Number(line.Line_Amount);
  if (!Number.isFinite(conIva) || !Number.isFinite(base)) return null;

  return {
    base: roundMoney(base, decimals),
    vat: subtractMoney(conIva, base, decimals),
  };
};

/** Calcula subtotal e IVA línea a línea
 * Soporta precios con y sin IVA (Price_Includes_VAT)
 *
 * `usarImportesDeBC` sólo se activa en documentos ya cerrados: mientras se
 * edita, el importe que trajo BC queda obsoleto en cuanto cambia la cantidad o
 * el precio, y la única verdad es rehacer la cuenta.
 */
const calculateLineTotals = (lines, country = null, usarImportesDeBC = false) => {
  const decimals = resolveMoneyDecimals(country);

  const lineAmounts = lines.map(
    (line) =>
      (usarImportesDeBC ? importesDeBusinessCentral(line, decimals) : null) ??
      calculateLineAmounts({
        unitPrice: line.Unit_Price,
        qty: line.Quantity,
        discountPct: line.Line_Discount_,
        ivaPct: line.PorIva,
        priceIncludesVAT: line.Price_Includes_VAT,
        country,
      })
  );

  return {
    subtotal: sumMoney(lineAmounts, (l) => l.base, decimals),
    vat: sumMoney(lineAmounts, (l) => l.vat, decimals),
  };
};

/** Descuento por método de pago según reglas */
/**
 * El descuento por forma de pago de una factura.
 *
 * Se exporta porque hasta ahora había **tres** implementaciones de este mismo
 * concepto, con criterios distintos: ésta (la que ve el cajero), la que se
 * guardaba en la factura y la que se le mandaba a Business Central. Con
 * descuento de línea y una sola forma de pago con descuento, la pantalla cobraba
 * un total y a BC se le mandaba otro.
 *
 * La regla, decidida el 25 de agosto de 2026 (decisión D5): **el descuento de
 * forma de pago se anula si hay cualquier otro descuento** -- venga del cupón,
 * del cliente o de la línea. Los tres terminan en `Line_Discount_`, así que
 * mirarlo a él los cubre a los tres.
 *
 * Y sólo aplica con **una** forma de pago: con varias no hay a cuál atribuirlo.
 */
export const calcularDescuentoDeFormaDePago = (lines = [], paymentLines = []) => {
  const hasSpecialDiscount = (lines || []).some((l) => (l.Line_Discount_ || 0) > 0);

  if (hasSpecialDiscount) return 0;
  if (!paymentLines || paymentLines.length === 0) return 0;
  if (paymentLines.length > 1) return 0;

  // toSafeNumber, el mismo normalizador que ya usan los importes de línea en
  // este archivo: el porcentaje sale de la tabla local y, si alguna vez llegara
  // como texto ("0", "5"), las comparaciones de InvoiceForm se partían en dos
  // --unas usaban == y otras ===-- y daban resultados distintos para el mismo
  // descuento.
  return toSafeNumber(paymentLines[0].Dto) || 0;
};

/**
 * El porcentaje de descuento por forma de pago de un documento.
 *
 * Se lee el PORCENTAJE y no el importe guardado a propósito. `Dto_Amount` se
 * calcula al cobrar como un porcentaje del importe **ya cobrado**
 * (InvoiceForm.js), no del bruto: en la factura PVMDSC1R-007671 el bruto es
 * 247,85, se cobraron 237,94 y el descuento real fue 9,91 -- pero quedó
 * guardado 9,52, que es el 4% de 237,94. Restar ese importe da 238,33, un
 * documento que nunca existió. Aplicar el porcentaje da 237,94, que es lo que
 * se cobró y lo que dice el cierre.
 *
 * Misma regla que `calcularDescuentoDeFormaDePago`: con más de una forma de
 * pago no hay descuento, porque no hay a cuál atribuirlo.
 *
 * @param {Array} payLines Líneas de pago del documento.
 * @param {boolean} esBC   Si vienen crudas de Business Central (Importe/Descuento)
 *                         en vez de locales (Amount/Dto).
 */
export const porcentajeDeDescuentoDePago = (payLines = [], esBC = false) => {
  if (!Array.isArray(payLines) || payLines.length !== 1) return 0;

  const linea = payLines[0];
  if (!esBC) return toSafeNumber(linea?.Dto);

  // BC no publica el porcentaje, sólo el importe.
  //
  // ATENCIÓN: dividir por el cobrado sólo devuelve el porcentaje original en
  // los documentos de MAYO DE 2026 EN ADELANTE (9,52 / 237,94 = 4%). En los
  // anteriores el descuento está tomado sobre el bruto, y esta división da de
  // más: PV5Y-0000030 tiene importe 232,05 y descuento 9,67, y sale 4,17% en
  // vez del 4% real.
  //
  // Medido el 06/09/2026 sobre los 8.222 pagos con descuento del ambiente de
  // pruebas de Ecuador: 5.159 son anteriores a mayo --y a todos ellos se les
  // deduce mal el porcentaje-- por 3.046 posteriores. El total que sale de
  // aplicarlo queda 1.179,82 por debajo de lo que se cobró, hasta 1,72 en un
  // solo documento (PVMDSC1-000509). Ver descuentoSobreElBruto.test.js.
  //
  // Por eso los informes ya NO usan esta función leyendo de BC: usan
  // `descuentoDePagoDelDocumento`, que no deduce ningún porcentaje del importe
  // del descuento. Esta se conserva para el camino local --donde `Dto` es el
  // porcentaje de verdad del catálogo, no una deducción-- y para las pruebas
  // que fijan el defecto.
  const importe = toSafeNumber(linea?.Importe);
  const descuento = toSafeNumber(linea?.Descuento);
  return importe > 0 ? roundDecimals((descuento * 100) / importe, 2) : 0;
};

/**
 * Aplica el descuento por forma de pago sobre el total con IVA.
 *
 * Es la misma cuenta que hace `calculateBaseTotals` para el documento en
 * pantalla, extraída para que los informes lleguen al mismo número.
 */
export const aplicarDescuentoDeFormaDePago = (bruto, porcentaje, country = null) => {
  const decimals = resolveMoneyDecimals(country);
  if (!porcentaje || porcentaje <= 0) return roundMoney(bruto, decimals);

  return roundMoney(bruto * (1 - porcentaje / 100), decimals);
};

/**
 * Lo que se cobró de verdad por un documento, y qué porcentaje representa.
 *
 * Existe porque **Business Central guarda el descuento con dos convenciones
 * distintas** y no hay forma fiable de saber cuál lleva cada fila:
 *
 *   · Hasta abril de 2026, el importe del descuento está tomado sobre el bruto
 *     (PV5Y-0000030: importe 232,05, descuento 9,67 = 4% de 241,72).
 *   · Desde mayo de 2026, sobre el ya cobrado (9,52 = 4% de 237,94).
 *
 * Medido el 06/09/2026 sobre los 8.222 pagos con descuento del ambiente de
 * pruebas de Ecuador: 5.159 de la primera y 3.046 de la segunda, con el corte
 * dentro de mayo. Deducir el porcentaje dividiendo por el cobrado acierta con
 * una población y falla con la otra, así que **no se deduce nada**: el campo
 * `Importe` es lo que entró en caja en las dos, y de ahí sale todo lo demás.
 *
 * El porcentaje se devuelve con cuatro decimales porque en el informe de
 * ventas por producto se aplica línea a línea: con dos, el reparto se aleja
 * del total del documento.
 *
 * @param {number}  bruto    Base + IVA del documento, antes de descontar.
 * @param {Array}   payLines Líneas de pago del documento.
 * @param {boolean} esBC     Si vienen crudas de BC (Importe/Descuento) o son
 *                           locales (Amount/Dto).
 * @param {object}  [country]
 * @returns {{total: number, porcentaje: number}}
 */
export const descuentoDePagoDelDocumento = (bruto, payLines = [], esBC = false, country = null) => {
  const decimals = resolveMoneyDecimals(country);
  const brutoRedondeado = roundMoney(bruto, decimals);
  const sinDescuento = { total: brutoRedondeado, porcentaje: 0 };

  // Con más de una forma de pago no hay descuento que atribuir (decisión D5).
  if (!Array.isArray(payLines) || payLines.length !== 1) return sinDescuento;

  const linea = payLines[0];

  if (!esBC) {
    // En local el porcentaje es el del catálogo, guardado al cobrar: es el
    // dato de verdad y no hay nada que deducir.
    const porcentaje = toSafeNumber(linea?.Dto);
    if (porcentaje <= 0) return sinDescuento;

    return { total: aplicarDescuentoDeFormaDePago(bruto, porcentaje, country), porcentaje };
  }

  if (toSafeNumber(linea?.Descuento) <= 0) return sinDescuento;

  // Sólo se toma `Importe` como total cuando hubo descuento: un documento sin
  // él puede tener un pago parcial, y ahí el importe cobrado no es su total.
  const cobrado = roundMoney(toSafeNumber(linea?.Importe), decimals);
  if (cobrado <= 0 || brutoRedondeado <= 0) return sinDescuento;

  return {
    total: cobrado,
    porcentaje: roundDecimals((1 - cobrado / brutoRedondeado) * 100, 4),
  };
};

/** Aplica descuento (si hay) y calcula totales base */
const calculateBaseTotals = (subtotal, vat, paymentMethodDiscount, country = null) => {
  const decimals = resolveMoneyDecimals(country);
  const baseTotal = addMoney(subtotal, vat, decimals);
  let total = baseTotal;

  if (paymentMethodDiscount > 0) {
    total = roundMoney(total * (1 - paymentMethodDiscount / 100), decimals);
  }

  return {
    baseTotal,
    total,
  };
};

/** Calcula total pagado y pendiente */
const calculatePaymentSummary = (paymentLines, total, country = null) => {
  const decimals = resolveMoneyDecimals(country);
  const paymentTotal = sumMoney(paymentLines, (l) => toSafeNumber(l.Amount, country), decimals);
  const pending = roundMoney(total - paymentTotal, decimals);

  return { paymentTotal, pending };
};

/**
 * Calcula todos los totales de una factura o nota de crédito.
 * Centraliza la lógica de cálculo (subtotal, IVA, descuento, ajuste)

 * 🇪🇨 Incluye la corrección del 4 % solicitada por Ecuador
 * para facturas con diferencias de redondeo
 *
 * @param {Object} invoice Factura o nota de crédito completa (con lines[])
 * @param {Array} paymentLines Líneas de pago asociadas
 * @returns {{
 *   subtotal: number,         
 *     → Total neto de las líneas sin IVA (ya con descuentos de línea aplicados).
 *
 *   vat: number,              
 *     → Total del impuesto IVA calculado sobre el subtotal.
 *
 *   baseTotal: number,        
 *     → Subtotal + VAT sin incluir descuento.
 *
 *   total: number,            
 *     → Total bruto del documento: subtotal + IVA.
 *       Si existe descuento por método de pago, el total reflejará el valor neto.
 *
 *   paymentTotal: number,     
 *     → Suma de todos los importes abonados en las líneas de pago.
 *
 *   pending: number,          
 *     → Diferencia entre el total del documento y el total pagado.
 *       Si está dentro de ±0.02, se considera tolerancia (sin ajuste).
 *
 *   totalAjustado: number,    
 *     → Total corregido después de aplicar el ajuste del 4 % 
 *       (solo en los casos donde se detectan descuentos erróneos).
 *
 *   ajuste: number,           
 *     → Diferencia exacta entre el total calculado y el total pagado.
 *       Representa el importe del ajuste aplicado (positivo o negativo).
 * }}
 */
export const calculateInvoiceTotalsHelper = (invoice, paymentLines = [], country = null) => {
  if (!invoice || !invoice.lines) return getEmptyTotals();

  // Un documento con No ya está cerrado en Business Central: no se puede editar
  // (InvoiceForm lo bloquea con ese mismo criterio), así que los importes que
  // trajo BC son la verdad y son los que cuadran contra el pago. Mientras se
  // edita no, porque cambian con cada línea que se toca.
  const documentoCerrado = invoice.posted === 1 || !!invoice.No;

  // Calcular subtotal e IVA línea a línea
  const { subtotal, vat } = calculateLineTotals(invoice.lines, country, documentoCerrado);

  // Determinar descuento de método de pago según reglas de Ecuador
  const paymentMethodDiscount = calcularDescuentoDeFormaDePago(invoice.lines, paymentLines);

  // Calcular totales base y aplicar descuento (si aplica)
  const { baseTotal, total } = calculateBaseTotals(subtotal, vat, paymentMethodDiscount, country);

  // Calcular totales de pago y pendiente
  const { paymentTotal, pending } = calculatePaymentSummary(paymentLines, Math.abs(total), country);

  // Aplicar corrección del 4 % (Ecuador)
  const { totalAjustado, ajuste } = applyConditionalAdjustment(
    invoice,
    paymentTotal,
    total,
    pending,
    paymentLines,
    country
  );

  return {
    subtotal,
    vat,
    baseTotal,
    total,
    paymentTotal,
    pending,
    totalAjustado,
    ajuste,
    paymentMethodDiscount,
  };
};
